/**
 * IndexedDB utility for caching dashboard snapshot data
 * Provides persistent caching that survives browser refreshes
 */

const DB_NAME = 'DashboardSnapshotCache';
const DB_VERSION = 1;
const STORE_NAME = 'chartData';
const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

class IndexedDBCache {
    constructor() {
        this.db = null;
        this.initPromise = this.init();
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                
                if (db.objectStoreNames.contains(STORE_NAME)) {
                    db.deleteObjectStore(STORE_NAME);
                }

                const store = db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
                store.createIndex('timestamp', 'timestamp', { unique: false });
                store.createIndex('organizationId', 'organizationId', { unique: false });
            };
        });
    }

    async ensureDB() {
        if (!this.db) {
            await this.initPromise;
        }
        return this.db;
    }

    generateCacheKey(organizationId, reportingPeriod, coverageTypes, statusValues) {
        // If only organizationId is provided, use it as the key
        if (!reportingPeriod && !coverageTypes && !statusValues) {
            return JSON.stringify({ organizationId: organizationId || "" });
        }
        
        // Otherwise, use full key structure
        const key = {
            organizationId: organizationId || "",
            reportingPeriod: reportingPeriod || "",
            coverageTypes: Array.isArray(coverageTypes) ? [...coverageTypes].sort((a, b) => a.localeCompare(b)).join(",") : "",
            statusValues: Array.isArray(statusValues) ? [...statusValues].sort((a, b) => a.localeCompare(b)).join(",") : "",
        };
        return JSON.stringify(key);
    }

    async saveToCacheDB(cacheKey, data, organizationId) {
        try {
            await this.ensureDB();
            await this.clearOrganizationCache(organizationId);

            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const cacheData = {
                cacheKey,
                organizationId,
                data,
                timestamp: Date.now(),
            };

            await new Promise((resolve, reject) => {
                const request = store.put(cacheData);
                request.onsuccess = () => {
                    resolve(true);
                };
                request.onerror = () => reject(request.error);
            });

            return true;
        } catch (error) {
            console.error('Error saving to IndexedDB cache:', error);
            return false;
        }
    }

    async getFromCacheDB(cacheKey) {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.get(cacheKey);

                request.onsuccess = (event) => {
                    const result = event.target.result;
                    
                    if (result) {
                        const isCacheValid = (Date.now() - result.timestamp) < CACHE_EXPIRY_MS;
                        
                        if (isCacheValid) {
                            resolve(result.data);
                        } else {
                            this.removeCachedData(cacheKey);
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                };

                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error retrieving from IndexedDB cache:', error);
            return null;
        }
    }

    async removeCachedData(cacheKey) {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            await new Promise((resolve, reject) => {
                const request = store.delete(cacheKey);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error removing cached data:', error);
        }
    }

    async clearOrganizationCache(organizationId) {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('organizationId');

            const request = index.openCursor(IDBKeyRange.only(organizationId));
            let deletedCount = 0;
            
            await new Promise((resolve, reject) => {
                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        cursor.delete();
                        deletedCount++;
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error clearing organization cache:', error);
        }
    }

    async clearAllExceptOrganization(keepOrganizationId) {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const request = store.openCursor();
            let deletedCount = 0;
            let keptCount = 0;

            await new Promise((resolve, reject) => {
                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        const record = cursor.value;
                        const recordOrgId = String(record.organizationId);
                        const keepOrgId = String(keepOrganizationId);
                        
                        if (recordOrgId === keepOrgId) {
                            keptCount++;
                        } else {
                            cursor.delete();
                            deletedCount++;
                        }
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error clearing cache for other organizations:', error);
        }
    }

    async clearAllCache() {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            await new Promise((resolve, reject) => {
                const request = store.clear();
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error clearing all cache:', error);
        }
    }

    async getAllCachedData() {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.getAll();

                request.onsuccess = (event) => {
                    const results = event.target.result;
                    resolve(results);
                };

                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error getting all cached data:', error);
            return [];
        }
    }

    async resetCacheExpiry() {
        try {
            await this.ensureDB();
            
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.openCursor();
            const currentTime = Date.now(); // Fresh timestamp = new 30-minute timer starts now
            let updatedCount = 0;

            await new Promise((resolve, reject) => {
                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        const record = cursor.value;
                        // Update timestamp to current time = reset 30-minute expiry timer
                        record.timestamp = currentTime;
                        cursor.update(record);
                        updatedCount++;
                        cursor.continue();
                    } else {
                        console.log(`Cache expiry reset for ${updatedCount} items - each now valid for 30 more minutes`);
                        resolve();
                    }
                };
                request.onerror = () => reject(request.error);
            });

            return true;
        } catch (error) {
            console.error('Error resetting cache expiry:', error);
            return false;
        }
    }
}

const indexedDBCache = new IndexedDBCache();

export const generateCacheKey = (...args) => indexedDBCache.generateCacheKey(...args);
export const saveToCacheDB = (...args) => indexedDBCache.saveToCacheDB(...args);
export const getFromCacheDB = (...args) => indexedDBCache.getFromCacheDB(...args);
export const clearOrganizationCache = (...args) => indexedDBCache.clearOrganizationCache(...args);
export const clearAllExceptOrganization = (...args) => indexedDBCache.clearAllExceptOrganization(...args);
export const clearAllCache = (...args) => indexedDBCache.clearAllCache(...args);
export const getAllCachedData = (...args) => indexedDBCache.getAllCachedData(...args);
export const resetCacheExpiry = (...args) => indexedDBCache.resetCacheExpiry(...args);

export default indexedDBCache;
